import React, {
  useState,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react'
import CustomIcons from './CustomIcons'
import theme from '../../styles/theme'
import { StandardButton } from './styles/Button.styles'
import {
  FilePanel,
  DropZone,
  FileInfo,
  FileName,
  UploadText,
  RemoveButton,
  FileContainer,
  UploaderContainer,
} from './styles/CustomFileUploader.styles'

// File data interface
interface FileData {
  name: string
  blobFile: File
  fileKey: number
}

// Modal component interface
interface ValidationModalProps {
  open: boolean
  handleClose: () => void
  onProceed: () => void
  onHideInFutureChanged?: (value: boolean) => void
}

// Component props interface
interface CustomFileUploaderProps {
  onFileSelect?: (files: FileData[]) => void
  onRemove?: () => void
  acceptedTypes?: string
  disabled?: boolean
  showBtn?: boolean
  btnText?: string
  ValidationModal?: React.ComponentType<ValidationModalProps> | null
  onHideInFutureChanged?: (value: boolean) => void
  initialFile?: FileData | null
}

// Ref interface for imperative methods
interface CustomFileUploaderRef {
  reset: () => void
}

const CustomFileUploader = forwardRef<
  CustomFileUploaderRef,
  CustomFileUploaderProps
>(
  (
    {
      onFileSelect,
      onRemove,
      acceptedTypes,
      disabled = false,
      showBtn = false,
      btnText = 'Select file',
      ValidationModal = null,
      onHideInFutureChanged,
      initialFile = null,
    },
    ref,
  ) => {
    const [isDragging, setIsDragging] = useState<boolean>(false)
    const [file, setFile] = useState<File | null>(null)
    const [showModal, setShowModal] = useState<boolean>(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => ({
      reset: () => {
        setFile(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      },
    }))

    useEffect(() => {
      if (initialFile) {
        setFile(initialFile.blobFile)
      }
    }, [initialFile])

    const triggerFileInput = (): void => {
      if (!disabled) {
        fileInputRef.current?.click()
      }
    }

    const handleModalProceed = (): void => {
      triggerFileInput()
    }

    const handleClick = (): void => {
      if (disabled) return

      if (ValidationModal) {
        setShowModal(true)
      } else {
        triggerFileInput()
      }
    }

    const handleDrag = useCallback(
      (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault()
        e.stopPropagation()
      },
      [],
    )

    const handleDragIn = useCallback(
      (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault()
        e.stopPropagation()
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
          setIsDragging(true)
        }
      },
      [],
    )

    const handleDragOut = useCallback(
      (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
      },
      [],
    )

    const handleDrop = useCallback(
      (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        if (disabled) return

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          const droppedFile = e.dataTransfer.files[0]
          handleFile(droppedFile)
          e.dataTransfer.clearData()
        }
      },
      /* eslint-disable-next-line */
      [disabled],
    )

    const handleFile = (selectedFile: File): void => {
      setFile(selectedFile)
      if (onFileSelect) {
        onFileSelect([
          {
            name: selectedFile.name,
            blobFile: selectedFile,
            fileKey: Date.now(),
          },
        ])
      }
    }

    const handleInputChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
      if (e.target.files && e.target.files.length > 0) {
        handleFile(e.target.files[0])
      }
    }

    const handleRemove = (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation()
      setFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      if (onRemove) {
        onRemove()
      }
    }

    return (
      <>
        <UploaderContainer>
          <FilePanel>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleInputChange}
              accept={acceptedTypes}
            />
            <DropZone
              onClick={handleClick}
              onDragEnter={handleDragIn}
              onDragLeave={handleDragOut}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              isDragging={isDragging}
              style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            >
              {file ? (
                <FileContainer>
                  <FileInfo>
                    <CustomIcons.faFile
                      size="small"
                      color={theme.colors.backgroundDark}
                    />
                    <FileName>{file.name}</FileName>
                  </FileInfo>
                  {!disabled && (
                    <RemoveButton
                      onClick={handleRemove}
                      type="button"
                      title="Remove file"
                    >
                      <CustomIcons.faTimes
                        size="small"
                        color={theme.colors.backgroundVeryDark}
                        hoverColor={theme.colors.error}
                      />
                    </RemoveButton>
                  )}
                </FileContainer>
              ) : (
                <UploadText>Click or drag a file to upload</UploadText>
              )}
            </DropZone>
          </FilePanel>
          {showBtn && (
            <StandardButton
              type="button"
              onClick={handleClick}
              disabled={disabled}
              style={{ backgroundColor: theme.colors.suplementYellow }}
            >
              {btnText}
            </StandardButton>
          )}
        </UploaderContainer>
        {ValidationModal && (
          <ValidationModal
            open={showModal}
            handleClose={() => setShowModal(false)}
            onProceed={handleModalProceed}
            onHideInFutureChanged={onHideInFutureChanged}
          />
        )}
      </>
    )
  },
)

CustomFileUploader.displayName = 'CustomFileUploader'

export default CustomFileUploader

export type {
  CustomFileUploaderProps,
  CustomFileUploaderRef,
  FileData,
  ValidationModalProps,
}
